import { Component, OnDestroy, OnInit } from '@angular/core';
import { TokenService } from 'services/token/token.service';
import { ActivatedRoute, Router } from '@angular/router';
import { LoginFormComponent } from 'pages/entry/login-form/login-form.component';
import { getSelfEnrollmentLink, LOGIN_ERRORS, RESPONSE_ERRORS, SIGN_IN_TYPES } from '../entry.helpers';
import { EntryService } from 'services/entry/entry.service';
import _get from 'lodash-es/get';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbstractLoginComponent } from 'pages/entry/login/login.component';
import { Observable } from 'rxjs';
import { ExternalLoginSettings } from 'interfaces/external-login.interface';
import { Selectors as LoginProviderSelectors } from 'services/login-provider/+state/login-provider.state';
import { map } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { LoginProviderRedirectResponse } from 'services/login-provider/login-provider.interface';

@UntilDestroy()
@Component({
  selector: 'cm-self-enrollment-login',
  styleUrls: ['./self-enrollment-login.component.scss'],
  templateUrl: './self-enrollment-login.component.html',
})
export class SelfEnrollmentLoginComponent extends AbstractLoginComponent implements OnInit, OnDestroy {
  private token: string;

  settings$: Observable<ExternalLoginSettings> = this.store
    .select(LoginProviderSelectors.loginProviderMappedState)
    .pipe(
      map((data) => ({
        ...data,
        url: window.location.href,
      })),
    );

  constructor(
    private tokenService: TokenService,
    private route: ActivatedRoute,
    private router: Router,
    private entryService: EntryService,
    private store: Store<any>,
  ) {
    super();
  }

  ngOnInit() {
    this.tokenService
      .getLoginType()
      .pipe(untilDestroyed(this))
      .subscribe((result) => {
        this.loginType = result;

        const queryParams = this.route.snapshot.queryParams;
        if (queryParams) {
          const { accessToken, type, organizationId, error } = queryParams as LoginProviderRedirectResponse;
          if ([SIGN_IN_TYPES.ONE_LOGIN, SIGN_IN_TYPES.OKTA, SIGN_IN_TYPES.AZURE, SIGN_IN_TYPES.ENTRA, SIGN_IN_TYPES.PING].includes(type)) {
            if (error === LOGIN_ERRORS.ACCESS_DENIED) {
              this.error = LOGIN_ERRORS.INCORRECT_ACCOUNT;
            } else if (accessToken) {
              this.loginType = type;
              this.onSelfEnrollmentLogin(accessToken, type, Number(organizationId));
            }
          }
        }
      });

    this.token = this.route.snapshot.params['token'];
  }

  ngOnDestroy() {}

  onSubmit(loginForm: LoginFormComponent): void {
    this.isSubmitted = true;
    this.error = '';

    const formData = loginForm.getFormData();
    if (!!formData) {
      const { username, password } = formData;
      const payload = { link: getSelfEnrollmentLink() };
      if (this.isDefaultLoginType) {
        this.tokenService.getUserSelfEnrollmentLogin({ username, password, payload }).subscribe(
          (errorMessage) => (this.error = errorMessage),
          (loginError) => this.handleError(loginError),
        );
      } else {
        this.tokenService.getMemberClicksUserSelfEnrollmentLogin({ username, password, payload }).subscribe(
          (errorMessage) => (this.error = errorMessage),
          (loginError) => this.handleError(loginError),
        );
      }
    }
  }

  onTokenLogin(response: any): void {
    const { credential: token, type } = response;
    this.loginType = type;
    this.onSelfEnrollmentLogin(token, type);
  }

  onSelfEnrollmentLogin(token: string, type: SIGN_IN_TYPES, organizationId?: number) {
    const payload = { link: getSelfEnrollmentLink() };
    this.tokenService.getUserSelfEnrollmentLogin({ token, signInType: type, payload, organizationId }).subscribe(
      (errorMessage) => (this.error = errorMessage),
      (loginError) => this.handleError(loginError),
    );
  }

  handleError(loginError) {
    // console.log(loginError);

    if (
      _get(loginError.body, 'errorCode') === RESPONSE_ERRORS.VALIDATION_ERROR ||
      _get(loginError.body, 'code_name') === RESPONSE_ERRORS.VALIDATION_ERROR
    ) {
      if (loginError.body.is_user_deleted) {
        this.entryService.setMessageType({ msg: 'ACCOUNT_DELETED', email: 'john@doe.com' });
        this.router.navigateByUrl('/entry/message');
      }
      const invalidLinkError = !!_get(loginError.body, 'details.link.length', 0);
      if (invalidLinkError) {
        this.router.navigateByUrl('/not-found');
      }

      if (!!_get(loginError.body, 'details.certificates.length')) {
        this.error = LOGIN_ERRORS.DRAFT_CERTIFICATION;
      }
    } else {
      super.handleError(loginError);
    }
  }

  back() {
    const { title, token } = this.route.snapshot.params;
    if (title && token) {
      return this.router.navigate([`/entry/self-enrollments/${title}/${token}`]);
    }
    return window.history.back();
  }
}
